import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { pricingModelSchema, PricingModelFormData } from '../schemas/model.schemas';
import { PricingModel } from '../types/model.types';
import { useModelStore } from '../store/model-store';
import { useToast } from '@/lib/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { PricingModelType } from '@/types';
import type { ModelTemplate } from '../data/model-templates';

const MODEL_TYPE_LABELS: Record<string, string> = {
  STANDARD: 'Estandar',
  SUBSCRIPTION: 'Suscripcion',
  BUNDLE: 'Bundle / Paquete',
  GOOD_BETTER_BEST: 'Bueno-Mejor-Optimo',
  CUSTOMER_SPECIFIC: 'Especifico por Cliente',
  TCO_ROI: 'TCO / ROI',
  PROMOTIONAL: 'Promocional',
  USAGE_BASED: 'Basado en Uso',
  COST_PLUS: 'Costo + Margen',
  DYNAMIC: 'Dinamico',
  LEASING: 'Leasing',
  MANAGED_SERVICE: 'Servicio Gestionado',
  TIERED_VOLUME: 'Volumen Escalonado',
  FREEMIUM: 'Freemium',
  PROJECT_BASED: 'Por Proyecto',
  HYBRID: 'Hibrido',
  AUCTION: 'Subasta / Licitación',
};

interface ModelFormProps {
  model?: PricingModel;
  template?: ModelTemplate | null;
  onSuccess: (createdModelId?: string) => void;
  onCancel: () => void;
}

export function ModelForm({ model, template, onSuccess, onCancel }: ModelFormProps) {
  const { createModel, createFromTemplate, updateModel, isLoading } = useModelStore();
  const { toast } = useToast();

  const hasTemplate = template && template.id !== 'custom' && template.variables.length > 0;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<PricingModelFormData>({
    resolver: zodResolver(pricingModelSchema),
    defaultValues: model
      ? {
          name: model.name,
          code: model.code || '',
          description: model.description || '',
          type: (model.type as PricingModelType) || PricingModelType.STANDARD,
          targetMarginPercentage: model.targetMarginPercentage,
          floorMarginPercentage: model.floorMarginPercentage,
        }
      : {
          name: template ? '' : '',
          code: '',
          description: template?.description || '',
          type: (template?.type as PricingModelType) || PricingModelType.STANDARD,
          targetMarginPercentage: template?.targetMarginPercentage ?? 25,
          floorMarginPercentage: template?.floorMarginPercentage ?? 15,
        },
  });

  const onSubmit = async (data: PricingModelFormData) => {
    try {
      if (model) {
        await updateModel(model.id, data);
        toast({
          title: 'Modelo actualizado',
          description: 'El modelo ha sido actualizado correctamente',
          variant: 'success',
        });
        onSuccess();
      } else if (hasTemplate) {
        const mappedVariables = template.variables.map((v) => ({
          code: v.code,
          name: v.name,
          variableType: v.variableType,
          defaultValue: v.defaultValue != null ? String(v.defaultValue) : undefined,
          formula: v.formula || undefined,
          description: undefined,
          order: v.order,
          lookupTableCode: undefined,
          priceRole: v.priceRole,
        }));
        const mappedLookupTables = template.lookupTables?.map((lt) => ({
          code: lt.name.toUpperCase().replace(/\s+/g, '_'),
          name: lt.name,
          rowDimension: (lt as any).rowDimension || 'Fila',
          colDimension: (lt as any).colDimension || 'Columna',
          data: (lt as any).data || { rows: [], cols: [], values: [] },
        }));
        const created = await createFromTemplate({
          name: data.name,
          // Sin esto el código que se escribe se perdía: el modelo quedaba «Código: null».
          code: data.code || undefined,
          description: data.description,
          type: data.type,
          targetMarginPercentage: data.targetMarginPercentage,
          floorMarginPercentage: data.floorMarginPercentage,
          variables: mappedVariables,
          lookupTables: mappedLookupTables,
        });
        toast({
          title: 'Modelo creado desde template',
          description: `Modelo con ${template.variables.length} variables creado correctamente`,
          variant: 'success',
        });
        onSuccess(created?.id);
      } else {
        const created = await createModel(data);
        toast({
          title: 'Modelo creado',
          description: 'Ahora puedes agregar variables al modelo',
          variant: 'success',
        });
        onSuccess(created?.id);
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Error al guardar el modelo',
        variant: 'destructive',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Nombre *</Label>
          <Input
            id="name"
            placeholder="Modelo Estándar"
            {...register('name')}
            error={!!errors.name}
            disabled={isLoading}
          />
          {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="code">Código *</Label>
          <Input
            id="code"
            placeholder="STD"
            {...register('code')}
            error={!!errors.code}
            disabled={isLoading}
            readOnly={!!model && !!model.code}
            className={`uppercase ${model && model.code ? 'bg-muted cursor-not-allowed' : ''}`}
          />
          {errors.code && <p className="text-sm text-destructive">{errors.code.message}</p>}
          <p className="text-xs text-muted-foreground">Identificador único del modelo</p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Descripción</Label>
        <Textarea
          id="description"
          placeholder="Describe este modelo de pricing..."
          rows={3}
          {...register('description')}
          disabled={isLoading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="type">Tipo de Modelo</Label>
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value || PricingModelType.STANDARD}
              onValueChange={field.onChange}
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un tipo" />
              </SelectTrigger>
              <SelectContent>
                {Object.values(PricingModelType).map((type) => (
                  <SelectItem key={type} value={type}>
                    {MODEL_TYPE_LABELS[type] || type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        <p className="text-xs text-muted-foreground">Estrategia de pricing del modelo</p>
      </div>

      {hasTemplate && (
        <div className="rounded-lg bg-primary/5 border border-primary/20 p-3">
          <p className="text-sm text-primary font-medium">Template: {template.name}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Se crearan {template.variables.length} variables pre-configuradas con formulas listas
            para usar
          </p>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Guardando...
            </>
          ) : model ? (
            'Actualizar'
          ) : (
            'Crear Modelo'
          )}
        </Button>
      </div>
    </form>
  );
}

export default ModelForm;
